/* eslint-disable @typescript-eslint/no-explicit-any */
import { CloseIcon } from "@chakra-ui/icons";
import { Box, Button, Collapse, HStack, Heading, Icon, IconButton, Spinner, Text, VStack } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { MdFilterList } from "react-icons/md";
import { HiLightningBolt } from "react-icons/hi";
import InfiniteScroll from "react-infinite-scroll-component";

// Components
import DetailField from "../../../shared/DetailElement/DetailField";

// Providers
import { useClaimDetails } from "../../../context-providers/claims-details-provider";

// Utils
import { UserRole, ACTIVITY_TYPE_AUTO_ASSIGNMENT } from "../../../utils/consts";
import { convertUnixTimestampToLocalDate, formatMixedCaseName, formatSnakeCaseName } from "../../../utils/helpers";
import * as jsondiffpatch from "jsondiffpatch";
import DiffModal from "../../../shared/JsonDiffViewer";

export default function ActivityPane() {
	const { activities, activityTypes, hasMoreActivities, handleLoadMoreActivities, handleFetchActivities } =
		useClaimDetails();
	const [filterActivityType, setFilterActivityType] = useState<string>("");
	const [appliedFilterActivityType, setAppliedFilterActivityType] = useState<string>("");
	const [filterRole, setFilterRole] = useState<string>("");
	const [appliedFilterRole, setAppliedFilterRole] = useState<string>("");
	const [showFilterActivityMenu, setShowFilterActivityMenu] = useState(false);
	const [selectedActivity, setSelectedActivity] = useState<any>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const clearFilter = () => {
		setShowFilterActivityMenu(false);
		setFilterActivityType("");
		setAppliedFilterActivityType("");
		setFilterRole("");
		setAppliedFilterRole("");
		// Refetch activities without filters
		handleFetchActivities(1, "", "");
	};

	const viewActivityDetails = (activityId: number, label?: string) => {
		const activity = activities?.find((a) => a.getId() === activityId);
		if (!activity) return;

		const activityLog = activity.getActivityLog();
		// First parse to get the actual JSON string
		const firstParse = JSON.parse(activityLog);
		const parsedLog = typeof firstParse === "string" ? JSON.parse(firstParse.replaceAll("\n", "\\n")) : firstParse;

		if (parsedLog.note) {
			setSelectedActivity(parsedLog.note.note);
		} else if (parsedLog.old && parsedLog.new) {
			const diff = getJsonDiff(parsedLog);
			setSelectedActivity(diff);
		} else {
			setSelectedActivity({ _activityPayload: parsedLog });
		}
		setIsModalOpen(true);
	};

	function getJsonDiff(parsedLog: any) {
		try {
			// Extract old and new objects, and billing_summary if present
			const { old, new: newData, billing_summary } = parsedLog;
			if (!old || !newData) {
				console.error("Error: 'old' or 'new' object is missing in activity log");
				return { error: "Invalid structure, missing 'old' or 'new' object" };
			}

			const diffpatcher = jsondiffpatch.create({
				objectHash: (obj: any) => obj.id || JSON.stringify(obj), // Match objects by id if available
				arrays: { detectMove: true }
			});

			const delta = diffpatcher.diff(old, newData);

			// Spread delta into a plain object so we can add custom properties
			const result: any = delta ? { ...delta } : {};

			// Include billing_summary in the result so DiffModal can display it
			if (billing_summary) {
				result._billing_summary = billing_summary;
			}

			return Object.keys(result).length > 0 ? result : { message: "No changes detected." };
		} catch (error) {
			console.error("Error parsing activity log:", error);
			return { error: "Invalid JSON format" };
		}
	}

	const shownActivitiesText = useMemo(() => {
		if (appliedFilterActivityType === "" && appliedFilterRole === "") return "All";
		if (appliedFilterActivityType !== "" && appliedFilterRole !== "") return "Role & Activity Type";
		if (appliedFilterActivityType === "" && appliedFilterRole !== "")
			return formatMixedCaseName(appliedFilterRole.toString()) + " Role";
		if (appliedFilterActivityType !== "" && appliedFilterRole === "")
			return formatSnakeCaseName(appliedFilterActivityType.toString()) + " Type";
	}, [appliedFilterRole, appliedFilterActivityType]);

	return (
		<VStack height="100%" minW="340px" bg="white" gap="0" display={"flex"} boxShadow="2px 5px 7px 3px rgb(0 0 0 / 5%)">
			<HStack width="100%" gap="0" py="5px" pl="3" pr="1" backgroundColor="gray.600">
				<Heading width="100%" size="sm" color="white" textAlign="left">
					Activity
				</Heading>
				<Button
					size="xs"
					pl={3}
					minW="unset"
					variant="ghost"
					color="white"
					colorScheme="whiteAlpha"
					onClick={() => setShowFilterActivityMenu(!showFilterActivityMenu)}>
					{shownActivitiesText}
					{appliedFilterActivityType === "" && appliedFilterRole === "" && (
						<>
							&nbsp;&nbsp;
							<MdFilterList fontSize="24px" />
						</>
					)}
				</Button>
				{!(appliedFilterActivityType === "" && appliedFilterRole === "") && (
					<IconButton
						size="xs"
						p="5px"
						h="20px"
						mr="9px"
						aria-label="sort"
						borderRadius="10px"
						minWidth="20px"
						icon={<CloseIcon fontSize="10px" />}
						onClick={() => clearFilter()}
					/>
				)}
			</HStack>
			<Collapse
				in={showFilterActivityMenu}
				animateOpacity={false}
				style={{ position: "absolute", top: "113px", width: "340px" }}>
				<Box
					p="3"
					bg="white"
					border="1px solid"
					borderColor="gray.400"
					rounded="md"
					shadow="md"
					borderTopRadius="0"
					borderBottomRadius="4px">
					<HStack>
						<Text flex="1" textAlign="right" mt="7px">
							Role
						</Text>
						<DetailField
							width="200px"
							type="select"
							fontSize="1rem"
							variant="outline"
							minWidth="96px"
							marginBottom="0px"
							isEdit={true}
							displayValue={""}
							value={filterRole}
							onTextChange={(event) => setFilterRole(event.target.value !== "null" ? event.target.value : "")}
							values={Object.values(UserRole)
								.concat("ClaimsSystem" as UserRole)
								.sort()
								.reduce(
									(obj, role) => {
										obj[role.toString()] = formatMixedCaseName(role.toString());
										return obj;
									},
									{ null: "Any" } as { [roleId: string]: string }
								)}
						/>
					</HStack>
					<HStack>
						<Text flex="1" textAlign="right" mt="7px">
							Activity Type
						</Text>
						<DetailField
							width="200px"
							type="select"
							fontSize="1rem"
							variant="outline"
							minWidth="96px"
							marginBottom="0px"
							isEdit={true}
							displayValue={""}
							value={filterActivityType}
							onTextChange={(event) => setFilterActivityType(event.target.value !== "null" ? event.target.value : "")}
							values={activityTypes?.reduce(
								(obj, activityType) => {
									obj[activityType.getId()] = activityType.getLabel();
									return obj;
								},
								{ null: "Any" } as { [roleId: string]: string }
							)}
						/>
					</HStack>
					<HStack mt={5} mb={2} gap="5" justifyContent="center">
						<Button
							width="100px"
							size="sm"
							colorScheme="blackAlpha"
							backgroundColor="gray.500"
							onClick={() => clearFilter()}>
							Clear
						</Button>
						<Button
							width="100px"
							size="sm"
							colorScheme="orange"
							onClick={() => {
								setShowFilterActivityMenu(false);
								setAppliedFilterActivityType(filterActivityType);
								setAppliedFilterRole(filterRole);
								// Refetch activities with filters
								handleFetchActivities(1, filterRole || undefined, filterActivityType || undefined);
							}}>
							Filter
						</Button>
					</HStack>
				</Box>
			</Collapse>
			<Box id="scrollableDiv" overflowY="auto" pb="4" pt="2" px="2" mb="10px" width="100%" height="100%">
				<InfiniteScroll
					dataLength={activities?.length ?? 0}
					next={() => handleLoadMoreActivities(appliedFilterRole || undefined, appliedFilterActivityType || undefined)}
					hasMore={hasMoreActivities}
					loader={
						<Box display="flex" justifyContent="center" py={2}>
							<Spinner size="sm" color="orange.500" />
						</Box>
					}
					scrollableTarget="scrollableDiv">
					{activities?.map((activity) => (
						<Box
							key={activity.getId()}
							padding="6px 8px"
							borderRadius="5px"
							mt={1}
							mb={4}
							backgroundColor="offWhite"
							whiteSpace="pre-line">
							<HStack alignItems="flex-start">
								<Box flex="1">
									<Text fontSize="12px">{activity.getUserName() ?? "Claims System"} &nbsp;</Text>
									<Text fontSize="12px" mb="1">
										{convertUnixTimestampToLocalDate(activity.getCreatedAt()).toLocaleString()}
									</Text>
								</Box>
								<HStack spacing={1}>
									<Text
										fontSize="xs"
										px="4px"
										py="1px"
										mt="2px"
										backgroundColor={activity.getActivityType()?.getColor() ?? "lightgray"}
										borderRadius="4px"
										textTransform="none">
										{activity.getActivityType()?.getLabel()}
									</Text>
								</HStack>
							</HStack>
							<HStack spacing={0} opacity="100%" fontStyle="italic">
								<Text>{activity.getActivityMessage()}</Text>
							</HStack>

							{/* Conditionally render details */}
							{activity.getActivityType()?.getLabel() === "General" ||
							activity.getActivityType()?.getLabel() === "Note" ? (
								<Button
									color="orange.500"
									variant="ghost"
									padding="0"
									__css={{ background: "transparent", fontWeight: 500 }}
									onClick={() => viewActivityDetails(activity.getId(), activity.getActivityType()?.getLabel())}>
									View
								</Button>
							) : null}
						</Box>
					))}
				</InfiniteScroll>
			</Box>
			<DiffModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} diff={selectedActivity} />
		</VStack>
	);
}
